import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLang } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { adminListReceipts, adminCreateReceipt, adminDeleteReceipt, adminGetReceipt, adminUpdateReceipt, adminListApplications, crmGetLead, adminReceiptPayLink } from '../../api';
import ReceiptView from '../../components/ReceiptView';
import { toast } from '../../hooks/use-toast';
import { Plus, Eye, Printer, Trash2, Search, CreditCard, Banknote, Building2, Smartphone, Wallet, X, Pencil, Link as LinkIcon, Copy, FileDown, Landmark } from 'lucide-react';
import { BankAccountsManager } from '../../components/admin/BankAccountsManager';
import { adminListBankAccounts } from '../../api';

const methodIcons = {
  bank: Building2, cash: Banknote, apple_pay: Smartphone, google_pay: Smartphone, card: CreditCard, cheque: Wallet
};

const methodLabels = {
  bank: { en: 'Bank Transfer', ar: 'تحويل بنكي' },
  cash: { en: 'Cash', ar: 'نقدي' },
  apple_pay: { en: 'Apple Pay', ar: 'Apple Pay' },
  google_pay: { en: 'Google Pay', ar: 'Google Pay' },
  card: { en: 'Card', ar: 'بطاقة' },
  cheque: { en: 'Cheque', ar: 'شيك' }
};

const emptyItem = () => ({ description: '', quantity: 1, unit_price: 0 });

const AdminReceipts = () => {
  const { lang } = useLang();
  const [receipts, setReceipts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportOpen, setExportOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const loadBanks = () => adminListBankAccounts().then(setBankAccounts).catch(() => {});
  useEffect(() => { loadBanks(); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [exportForm, setExportForm] = useState({ date_from: firstOfMonth, date_to: today, status: 'all' });

  const doExport = async () => {
    // Refresh receipts data first so the PDF reflects the latest edits.
    await load();
    const p = new URLSearchParams();
    if (exportForm.date_from) p.set('date_from', exportForm.date_from);
    if (exportForm.date_to) p.set('date_to', exportForm.date_to);
    if (exportForm.status && exportForm.status !== 'all') p.set('status', exportForm.status);
    p.set('_t', String(Date.now()));
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    window.open(`${base}/api/admin/receipts/export.pdf?${p.toString()}`, '_blank');
    setExportOpen(false);
  };

  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    application_id: '', payment_method: 'bank', payment_reference: '',
    description: '', items: [emptyItem()], vat_percent: 5, vat_inclusive: true, currency: 'AED', notes: '', status: 'paid', issued_at: ''
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([adminListReceipts(), adminListApplications()]);
      setReceipts(r || []);
      setApplications(a || []);
    }
    catch { toast({ title: 'Load failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Handle deep-link from AdminApplications "Create Receipt" (?fromApp=APP-ID) OR from CRM (?fromLead=LID-ID)
  useEffect(() => {
    const fromApp = searchParams.get('fromApp');
    const fromLead = searchParams.get('fromLead');
    if (fromApp && applications.length && !createOpen && !editingId) {
      const app = applications.find((x) => x.id === fromApp);
      if (app) {
        pickApplication(app);
        setCreateOpen(true);
        searchParams.delete('fromApp');
        setSearchParams(searchParams, { replace: true });
      }
    } else if (fromLead && !createOpen && !editingId) {
      (async () => {
        try {
          const lead = await crmGetLead(fromLead);
          // If lead is linked to an app, pre-fill via app; otherwise use lead info directly
          if (lead.source === 'application' && lead.source_ref) {
            const app = applications.find((x) => x.id === lead.source_ref);
            if (app) pickApplication(app);
            else pickLead(lead);
          } else {
            pickLead(lead);
          }
          setCreateOpen(true);
          searchParams.delete('fromLead');
          setSearchParams(searchParams, { replace: true });
        } catch {
          toast({ title: lang === 'ar' ? 'تعذر تحميل العميل المحتمل' : 'Failed to load lead', variant: 'destructive' });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, searchParams]);

  const pickLead = (lead) => {
    if (!lead) return;
    setForm((f) => ({
      ...f,
      customer_name: lead.name || f.customer_name,
      customer_email: lead.email || f.customer_email,
      customer_phone: lead.phone || f.customer_phone,
      application_id: lead.source === 'application' ? (lead.source_ref || '') : f.application_id,
      currency: lead.currency || f.currency,
      description: f.description || (lang === 'ar' ? `دفعة للعميل ${lead.id}` : `Payment for lead ${lead.id}`),
      items: (f.items.length === 1 && !f.items[0].description && !Number(f.items[0].unit_price))
        ? [{ description: lead.company || 'Consultation service', quantity: 1, unit_price: Number(lead.value_estimate) || 0 }]
        : f.items,
    }));
  };

  const pickApplication = (app) => {
    if (!app) return;
    setForm((f) => ({
      ...f,
      application_id: app.id,
      customer_name: app.full_name || f.customer_name,
      customer_email: app.email || f.customer_email,
      customer_phone: app.phone || f.customer_phone,
      items: (f.items.length === 1 && !f.items[0].description && !Number(f.items[0].unit_price))
        ? (app.estimate && Number(app.estimate.total) > 0
          ? [
            ['license', lang === 'ar' ? 'رسوم الرخصة والتسجيل' : 'License & Registration Fees'],
            ['office', `${lang === 'ar' ? 'المكتب' : 'Office Setup'} (${{ flexi: 'Flexi Desk', private: 'Private Office', executive: 'Executive Suite' }[app.office_type] || app.office_type || '-'})`],
            ['visa', lang === 'ar' ? 'معالجة التأشيرات' : 'Visa Processing'],
            ['admin', lang === 'ar' ? 'الأتعاب المهنية والمعاملات' : 'Professional & PRO Fees'],
            ['tax', lang === 'ar' ? 'التسجيل الضريبي' : 'Tax Registration'],
          ].filter(([k]) => Number(app.estimate[k]) > 0).map(([k, description]) => ({ description, quantity: 1, unit_price: Number(app.estimate[k]) }))
          : [{ description: `${app.company_name || 'Business Setup'} — ${app.zone_name || app.zone_code || app.type}`, quantity: 1, unit_price: 0 }])
        : f.items,
      description: f.description || (lang === 'ar' ? `دفعة تأسيس شركة (${app.id})` : `Setup payment for ${app.id}`),
      notes: f.notes || app.pricing_notes || ''
    }));
  };

  const resetForm = () => setForm({ customer_name: '', customer_email: '', customer_phone: '', application_id: '', payment_method: 'bank', payment_reference: '', description: '', items: [emptyItem()], vat_percent: 5, vat_inclusive: true, currency: 'AED', notes: '', status: 'paid', issued_at: '' });

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const gross = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const vatPct = Number(form.vat_percent) || 0;
  const subtotal = form.vat_inclusive ? (vatPct ? gross / (1 + vatPct / 100) : gross) : gross;
  const vat = form.vat_inclusive ? gross - subtotal : gross * vatPct / 100;
  const total = form.vat_inclusive ? gross : gross + vat;

  const submit = async () => {
    if (!form.customer_name || form.items.length === 0 || form.items.some((it) => !it.description || !it.unit_price)) {
      toast({ title: lang === 'ar' ? 'أكمل الحقول المطلوبة' : 'Complete required fields', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_email: form.customer_email || undefined,
        application_id: form.application_id || null,
        items: form.items.map((it) => ({ description: it.description, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 })),
        vat_percent: Number(form.vat_percent) || 0,
        vat_inclusive: !!form.vat_inclusive,
        issued_at: form.issued_at ? new Date(form.issued_at).toISOString() : undefined
      };
      let rec;
      if (editingId) {
        rec = await adminUpdateReceipt(editingId, payload);
        toast({ title: lang === 'ar' ? 'تم حفظ التعديلات' : 'Receipt updated', description: rec.id });
      } else {
        rec = await adminCreateReceipt(payload);
        toast({ title: lang === 'ar' ? 'تم إصدار الإيصال' : 'Receipt issued', description: rec.id });
      }
      setCreateOpen(false);
      setEditingId(null);
      resetForm();
      setViewing(rec);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      toast({ title: detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const openEdit = async (r) => {
    try {
      const full = await adminGetReceipt(r.id);
      setForm({
        customer_name: full.customer_name || '',
        customer_email: full.customer_email || '',
        customer_phone: full.customer_phone || '',
        application_id: full.application_id || '',
        payment_method: full.payment_method,
        payment_reference: full.payment_reference || '',
        description: full.description || '',
        items: full.items.map((it) => ({ description: it.description, quantity: it.quantity, unit_price: it.unit_price })),
        vat_percent: full.vat_percent,
        vat_inclusive: full.vat_inclusive !== false,
        currency: full.currency,
        notes: full.notes || '',
        status: full.status,
        issued_at: full.issued_at ? new Date(full.issued_at).toISOString().slice(0, 16) : ''
      });
      setEditingId(full.id);
      setCreateOpen(true);
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const doPrint = () => {
    if (!viewing) return;
    const receiptEl = printRef.current;
    if (!receiptEl) return window.print();
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { window.print(); return; }
    // Collect all stylesheet links + inline styles from parent
    const head = Array.from(document.head.querySelectorAll('link[rel=stylesheet], style'))
      .map((n) => n.outerHTML).join('\n');
    const html = `<!DOCTYPE html><html lang="${lang}" dir="${lang === 'ar' ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>${viewing.id}</title>${head}<style>
      body{margin:0;padding:24px;background:#fff;color:#f8fafc;}
      .receipt-printable{box-shadow:none !important;max-width:100% !important;margin:0 !important;}
      @page { size: A4; margin: 12mm; }
      @media print { body { padding:0; } }
    </style></head><body>${receiptEl.outerHTML}<script>window.onload=()=>{setTimeout(()=>{window.print();},400);};window.onafterprint=()=>{window.close();};</script></body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  const remove = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'حذف الإيصال؟' : 'Delete receipt?')) return;
    try { await adminDeleteReceipt(id); load(); toast({ title: lang === 'ar' ? 'تم الحذف' : 'Deleted' }); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const generatePayLink = async (r) => {
    if (r.status === 'paid') {
      toast({ title: lang === 'ar' ? 'الإيصال مدفوع بالفعل' : 'Receipt already paid' });
      return;
    }
    try {
      const { checkout_url } = await adminReceiptPayLink(r.id);
      try { await navigator.clipboard.writeText(checkout_url); } catch { /* ignore */ }
      toast({ title: lang === 'ar' ? 'تم نسخ رابط الدفع' : 'Payment link copied' });
      window.open(checkout_url, '_blank');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed';
      toast({ title: msg, variant: 'destructive' });
    }
  };

  const filtered = receipts.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.id.toLowerCase().includes(q) || r.customer_name.toLowerCase().includes(q) || (r.customer_email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'إيصالات الدفع' : 'Payment Receipts'}</h1>
          <p className="text-sm text-[#111827]/60 mt-2">{receipts.length} {lang === 'ar' ? 'إيصال' : 'receipts'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setExportOpen(true)} variant="outline" className="rounded-md h-11 px-5 text-sm border-[#2563eb]/40 text-[#2563eb] hover:bg-[#2563eb]/8" data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 me-2" /> {lang === 'ar' ? 'كشف PDF' : 'Export PDF'}
          </Button>
          <Button onClick={() => setBankOpen(true)} variant="outline" className="rounded-md h-11 px-4 text-sm border-[#2563eb]/40 text-[#2563eb]" data-testid="bank-accounts-btn">
            <Landmark className="w-4 h-4 me-2" /> {lang === 'ar' ? 'الحسابات البنكية' : 'Bank accounts'}
          </Button>
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} className="gold-button rounded-md h-11 px-5 text-sm">
            <Plus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'إصدار إيصال' : 'New Receipt'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="glass-card rounded-xl p-3 flex items-center gap-2 no-print">
        <Search className="w-4 h-4 text-[#2563eb]/70 ms-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === 'ar' ? 'بحث برقم الإيصال أو الاسم' : 'Search by ID, name, email'} className="border-0 bg-transparent text-[#111827] focus-visible:ring-0" />
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden no-print">
        {loading ? <div className="p-10 text-center text-[#111827]/60">…</div>
        : filtered.length === 0 ? <div className="p-10 text-center text-[#111827]/60">{lang === 'ar' ? 'لا توجد إيصالات' : 'No receipts yet'}</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#2563eb]/8 border-b border-[#2563eb]/15">
                <tr className="text-[10px] uppercase tracking-widest text-[#2563eb]">
                  <th className="p-3 text-start">{lang === 'ar' ? 'رقم الإيصال' : 'Receipt #'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'العميل' : 'Customer'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الدفع' : 'Payment'}</th>
                  <th className="p-3 text-end">{lang === 'ar' ? 'الإجمالي' : 'Total'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const MIcon = methodIcons[r.payment_method];
                  return (
                    <tr key={r.id} className="border-b border-[#2563eb]/8 hover:bg-[#2563eb]/4">
                      <td className="p-3 font-mono text-xs text-[#3b82f6]">{r.id}</td>
                      <td className="p-3"><div className="text-[#0a0a0a]">{r.customer_name}</div><div className="text-[10px] text-[#111827]/50">{r.customer_email || '—'}</div>{r.application_id && <div className="text-[10px] text-[#2563eb] font-mono mt-0.5" title={lang === 'ar' ? 'مربوط بطلب' : 'Linked application'}>↳ {r.application_id}</div>}</td>
                      <td className="p-3">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#111827]/80">
                          {MIcon && <MIcon className="w-3.5 h-3.5" />}
                          {methodLabels[r.payment_method]?.[lang] || r.payment_method}
                        </div>
                      </td>
                      <td className="p-3 text-end text-[#3b82f6] font-medium">{r.total.toLocaleString(undefined, { minimumFractionDigits: 2 })} {r.currency}</td>
                      <td className="p-3 text-xs text-[#111827]/70">{new Date(r.issued_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${r.status === 'paid' ? 'bg-blue-500/15 text-blue-400 border-blue-400/30' : r.status === 'pending' ? 'bg-amber-500/15 text-amber-400 border-amber-400/30' : 'bg-rose-500/15 text-rose-400 border-rose-400/30'}`}>{r.status}</span>
                      </td>
                      <td className="p-3 flex items-center gap-1">
                        <Button onClick={() => setViewing(r)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title="View"><Eye className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => openEdit(r)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                        {r.status !== 'paid' && (
                          <Button onClick={() => generatePayLink(r)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent border-[#2563eb]/30 text-[#2563eb]" title={lang === 'ar' ? 'إنشاء رابط دفع (Geidea)' : 'Create Geidea pay link'} data-testid={`pay-link-${r.id}`}>
                            <LinkIcon className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(r.checkout_url || r.stripe_checkout_url) && r.status !== 'paid' && (
                          <Button onClick={async () => { try { await navigator.clipboard.writeText(r.checkout_url || r.stripe_checkout_url); toast({ title: lang === 'ar' ? 'تم النسخ' : 'Copied' }); } catch { /* ignore */ } }} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={lang === 'ar' ? 'نسخ الرابط' : 'Copy pay link'}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button onClick={() => remove(r.id)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent border-rose-400/30 text-rose-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEditingId(null); resetForm(); } }}>
        <DialogContent className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0a0a0a] font-display text-2xl">{editingId ? (lang === 'ar' ? `تعديل الإيصال ${editingId}` : `Edit Receipt ${editingId}`) : (lang === 'ar' ? 'إصدار إيصال دفع جديد' : 'Issue New Receipt')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer info */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'اسم العميل *' : 'Customer Name *'}</Label><Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'الإيميل' : 'Email'}</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label><Input value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'ربط بطلب موجود (اختياري)' : 'Link to Existing Application (optional)'}</Label>
                <Select value={form.application_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') { setForm({...form, application_id: ''}); return; }
                  const app = applications.find((a) => a.id === v);
                  if (app) pickApplication(app);
                }}>
                  <SelectTrigger data-testid="link-application-select" className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"><SelectValue placeholder={lang === 'ar' ? 'اختر طلباً لتعبئة بيانات العميل تلقائياً…' : 'Pick an application to auto-fill customer…'} /></SelectTrigger>
                  <SelectContent className="bg-white border-[#2563eb]/25 max-h-72">
                    <SelectItem value="none" className="text-[#111827]/70">{lang === 'ar' ? '— بدون ربط —' : '— No link —'}</SelectItem>
                    {applications.map((a) => (
                      <SelectItem key={a.id} value={a.id} className="text-[#111827]">
                        <span className="font-mono text-[#2563eb]">{a.id}</span>
                        <span className="text-[#111827]/60"> · {a.full_name} · {a.company_name || a.zone_name || a.zone_code || a.type}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.application_id && (
                  <div className="mt-1.5 text-[11px] text-[#2563eb]">
                    {lang === 'ar' ? 'مربوط بـ' : 'Linked to'}: <span className="font-mono">{form.application_id}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                    {Object.keys(methodLabels).map((k) => <SelectItem key={k} value={k} className="text-[#111827]">{methodLabels[k][lang]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'مرجع الدفع (اختياري)' : 'Payment Reference'}</Label><Input value={form.payment_reference} onChange={(e) => setForm({...form, payment_reference: e.target.value})} placeholder={lang === 'ar' ? 'رقم التحويل، الشيك، إلخ' : 'Transaction ID, Cheque #'} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'الحساب البنكي على الإيصال' : 'Bank account shown on receipt'}</Label>
              <Select value={form.bank_account_id || 'default'} onValueChange={(v) => setForm({ ...form, bank_account_id: v === 'default' ? '' : v })}>
                <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="receipt-bank-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                  <SelectItem value="default" className="text-[#111827]">{lang === 'ar' ? 'الحساب الافتراضي' : 'Default account'}{bankAccounts.find((b) => b.is_default) ? ` — ${bankAccounts.find((b) => b.is_default).label}` : ''}</SelectItem>
                  {bankAccounts.map((b) => <SelectItem key={b.id} value={b.id} className="text-[#111827]">{b.label} · {b.iban}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Receipt date (optional override) */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">
                {lang === 'ar' ? 'تاريخ الإيصال (اختياري)' : 'Receipt Date (optional)'}
              </Label>
              <Input
                type="datetime-local"
                value={form.issued_at || ''}
                onChange={(e) => setForm({ ...form, issued_at: e.target.value })}
                className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"
                data-testid="receipt-issued-at-input"
              />
              <div className="text-[10px] text-[#111827]/50 mt-1">
                {lang === 'ar' ? 'اتركه فارغاً لاستخدام التاريخ والوقت الحالي' : 'Leave empty to use current date & time'}
              </div>
            </div>

            <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'وصف الإيصال' : 'Description'}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'البنود *' : 'Line Items *'}</Label>
                <Button onClick={addItem} className="ghost-button rounded-md h-7 px-3 text-[10px] bg-transparent"><Plus className="w-3 h-3 me-1" />{lang === 'ar' ? 'أضف بند' : 'Add Item'}</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_80px_120px_36px] gap-2 items-center">
                    <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder={lang === 'ar' ? 'وصف' : 'Description'} className="h-10 bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] text-sm" />
                    <Input type="number" min="1" step="any" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="Qty" className="h-10 bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] text-sm" />
                    <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} placeholder={lang === 'ar' ? 'المبلغ (شامل الضريبة)' : 'Amount (VAT-incl)'} title={lang === 'ar' ? 'المبلغ شامل ضريبة القيمة المضافة' : 'Amount including VAT'} className="h-10 bg-[#f8fafc] border-[#2563eb]/25 text-[#111827] text-sm" />
                    {form.items.length > 1 && <Button onClick={() => removeItem(idx)} className="ghost-button rounded-md h-10 px-2 bg-transparent border-rose-400/30 text-rose-400"><X className="w-3.5 h-3.5" /></Button>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'ضريبة %' : 'VAT %'}</Label><Input type="number" min="0" max="100" step="0.5" value={form.vat_percent} onChange={(e) => setForm({...form, vat_percent: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'العملة' : 'Currency'}</Label><Input value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value.toUpperCase()})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'الحالة' : 'Status'}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                    <SelectItem value="paid" className="text-[#111827]">{lang === 'ar' ? 'مدفوع' : 'Paid'}</SelectItem>
                    <SelectItem value="pending" className="text-[#111827]">{lang === 'ar' ? 'معلق' : 'Pending'}</SelectItem>
                    <SelectItem value="refunded" className="text-[#111827]">{lang === 'ar' ? 'مسترد' : 'Refunded'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* VAT-inclusive toggle */}
            <label className="flex items-center gap-2 text-xs text-[#111827]/80 cursor-pointer select-none" data-testid="vat-inclusive-toggle">
              <input type="checkbox" checked={!!form.vat_inclusive} onChange={(e) => setForm({...form, vat_inclusive: e.target.checked})} className="w-4 h-4 accent-[#2563eb]" />
              <span>{lang === 'ar' ? 'المبلغ المُدخل شامل الضريبة (يُحسب مقدار الضريبة من المبلغ)' : 'Entered amount is VAT-inclusive (VAT is derived from the amount)'}</span>
            </label>

            <div><Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'ملاحظات' : 'Notes'}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" /></div>

            {/* Totals preview */}
            <div className="bg-[#f8fafc] rounded-lg p-4 space-y-1 text-sm" data-testid="totals-preview">
              <div className="flex justify-between text-[#111827]/70"><span>{lang === 'ar' ? 'المجموع الفرعي (قبل الضريبة)' : 'Subtotal (before VAT)'}</span><span className="tabular-nums">{subtotal.toFixed(2)} {form.currency}</span></div>
              <div className="flex justify-between text-[#111827]/70"><span>{lang === 'ar' ? (form.vat_inclusive ? 'من ضمنها ضريبة' : 'ضريبة') : (form.vat_inclusive ? 'VAT included' : 'VAT')} ({form.vat_percent}%)</span><span className="tabular-nums">{vat.toFixed(2)} {form.currency}</span></div>
              <div className="flex justify-between text-[#3b82f6] font-semibold text-base pt-2 border-t border-[#2563eb]/15"><span>{lang === 'ar' ? 'الإجمالي المستحق' : 'Total Due'}</span><span className="tabular-nums">{total.toFixed(2)} {form.currency}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setCreateOpen(false); setEditingId(null); }} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={submit} disabled={saving} className="gold-button rounded-md h-10 px-6 text-xs">{saving ? '…' : editingId ? (lang === 'ar' ? 'حفظ التعديلات' : 'Save Changes') : (lang === 'ar' ? 'إصدار وحفظ' : 'Issue Receipt')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Receipt Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white border-none max-w-4xl max-h-[95vh] overflow-y-auto p-0">
          <div className="p-4 flex justify-end gap-2 border-b border-gray-200 no-print bg-gray-50">
            <Button onClick={doPrint} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-md h-9 px-4 text-xs">
              <Printer className="w-3.5 h-3.5 me-2" /> {lang === 'ar' ? 'طباعة / حفظ PDF' : 'Print / Save PDF'}
            </Button>
            <Button onClick={() => setViewing(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md h-9 px-4 text-xs">{lang === 'ar' ? 'إغلاق' : 'Close'}</Button>
          </div>
          <ReceiptView ref={printRef} receipt={viewing} lang={lang} />
        </DialogContent>
      </Dialog>

      <BankAccountsManager open={bankOpen} onClose={() => setBankOpen(false)} lang={lang} onChanged={loadBanks} />

      {/* Export PDF Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] max-w-md" data-testid="export-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0a0a0a] flex items-center gap-2">
              <FileDown className="w-5 h-5 text-[#2563eb]" />
              {lang === 'ar' ? 'كشف الإيصالات PDF' : 'Payment Receipts PDF'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'من تاريخ' : 'From'}</Label>
                <Input type="date" value={exportForm.date_from} onChange={(e) => setExportForm({ ...exportForm, date_from: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="export-date-from" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'إلى تاريخ' : 'To'}</Label>
                <Input type="date" value={exportForm.date_to} onChange={(e) => setExportForm({ ...exportForm, date_to: e.target.value })} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="export-date-to" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2563eb] mb-1 block">{lang === 'ar' ? 'الحالة' : 'Status'}</Label>
              <Select value={exportForm.status} onValueChange={(v) => setExportForm({ ...exportForm, status: v })}>
                <SelectTrigger className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" data-testid="export-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                  <SelectItem value="all" className="text-[#111827]">{lang === 'ar' ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="paid" className="text-[#111827]">Paid</SelectItem>
                  <SelectItem value="pending" className="text-[#111827]">Pending</SelectItem>
                  <SelectItem value="refunded" className="text-[#111827]">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-[#111827]/60 rounded-md bg-[#f8fafc] p-3 border border-[#e5e7eb]">
              {lang === 'ar' ? 'سيفتح PDF في تبويب جديد وتظهر نافذة الطباعة تلقائياً لتحفظه على جهازك.' : 'A PDF preview will open in a new tab and the print dialog will appear automatically so you can save it.'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)} className="border-[#e5e7eb]">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={doExport} className="gold-button" data-testid="export-pdf-generate">
              <FileDown className="w-4 h-4 me-2" /> {lang === 'ar' ? 'توليد PDF' : 'Generate PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReceipts;
